// ============================================================================
// ROOM CATEGORY HOOKS
// ============================================================================
// React hooks for Room Category API operations

import { useState, useEffect, useCallback } from 'react';
import { roomCategoryService } from 'api/services/roomCategoryService';
import type {
  RoomCategory,
  CreateRoomCategoryRequest,
  UpdateRoomCategoryRequest,
  RoomCategorySearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all room categories (paginated)
 */
export const useRoomCategories = (params?: RoomCategorySearchRequest) => {
  const [data, setData] = useState<RoomCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchRoomCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roomCategoryService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRoomCategories();
  }, [fetchRoomCategories]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchRoomCategories,
  };
};

/**
 * Hook to fetch a single room category by ID
 */
export const useRoomCategory = (id: ID) => {
  const [data, setData] = useState<RoomCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRoomCategory = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await roomCategoryService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRoomCategory();
  }, [fetchRoomCategory]);

  return {
    data,
    loading,
    error,
    refetch: fetchRoomCategory,
  };
};

/**
 * Hook to create a new room category
 */
export const useCreateRoomCategory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRoomCategory = async (
    data: CreateRoomCategoryRequest,
    options?: {
      onSuccess?: (roomCategory: RoomCategory) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await roomCategoryService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createRoomCategory,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a room category
 */
export const useUpdateRoomCategory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRoomCategory = async (
    id: ID,
    data: UpdateRoomCategoryRequest,
    options?: {
      onSuccess?: (roomCategory: RoomCategory) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await roomCategoryService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateRoomCategory,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a room category
 */
export const useDeleteRoomCategory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRoomCategory = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await roomCategoryService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteRoomCategory,
    loading,
    error,
    success,
  };
};
